import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import LocalizedText from '@LocalizedText';
import { startLoading, endLoading } from '@app/actions/spinnerActions';
import { addWarning, addError } from '@app/actions/popupsActions';
import { setFooterConfig } from '@app/actions/footerActions';
import { resetCustomizeData } from '@app/actions/customizeActions';
import {
  stateArtworkInfo,
  resetArtworkInfo,
  finishArtwork,
  renameArtwork,
  renameDesign,
  resetArtworkImage,
  stateArtworkProject,
  createArtworkProject,
  processFinishedArtwork,
} from '@app/actions/artworkDataActions';
import {
  fetchArtworkUploadResult,
  uploadArtworkToProject,
  processArtworkUpload,
  setAssignedArtworkId,
} from '@app/actions/uploadActions';
import {
  getFileExt,
  getFileName,
  getFileType,
  isApplyCodesViewAvailable,
  parseError,
  isMobileMode,
  isStickerFlow,
  isSheetFlow,
  isRollFlow,
  isSkipNameStep,
  isValidArtwork,
} from '@app/utils/utils';
import { toTwips } from '@app/utils/measurement';
import { CUSTOMIZE_VIEW, APPLY_CODES_VIEW, ERROR_MODE_VIEW } from '@app/constants';
import { inject } from '@app/providers/QAU.ContextProvider';
import { navigateToView } from '@app/router';

import UploadArtwork from './upload-artwork/UploadArtwork';
import RenameDesign from './rename-design/RenameDesign';
import './UploadView.scss';

const isFileFormatInvalid = (file, fc = {}) => {
  const fileExt = getFileExt(file);
  const extensions = new Set();
  const fcFormats = fc.imageProcessing?.formats?.items || [];
  fcFormats.forEach(item => extensions.add(item.extension));

  return !extensions.has(fileExt);
};

const UploadView = ({
  matrix: { design = {}, ctx = {}, customize = {} },
  processFinishedArtwork,
  processArtworkUpload,
  setAssignedArtworkId,
  assignedArtworkId,
  fetchArtworkUploadResult,
  createArtworkProject,
  stateArtworkProject,
  uploadArtworkToProject,
  stateArtworkInfo,
  resetArtworkInfo,
  resetArtworkImage,
  resetCustomizeData,
  setFooterConfig,
  artworkProject,
  renameArtwork,
  finishArtwork,
  startLoading,
  renameDesign,
  artworkInfo,
  flowConfig,
  endLoading,
  addWarning,
  addError,
  context,
  flow,
}) => {
  const [designName, setDesignName] = useState(null);
  const [selectedFileData, setFileData] = useState(null);
  const [showRenameStep, setShowRenameStep] = useState(false);

  const convertToTwips = useCallback(
    value => {
      const {
        locale: { measurementSystem = 'imperial' },
      } = flowConfig;

      return toTwips(value, measurementSystem);
    },
    [flowConfig],
  );

  const resetSelectedFile = useCallback(() => {
    const { artwork_id } = artworkInfo;
    setFileData(null);
    resetArtworkInfo();
    resetArtworkImage();
    setAssignedArtworkId();
    setShowRenameStep(false);
    artwork_id && stateArtworkInfo({ artwork_id });
  }, [setAssignedArtworkId, resetArtworkImage, resetArtworkInfo, artworkInfo, stateArtworkInfo]);

  const prepareArtworkProject = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const { cornerRadius, width, height, shape } = ctx;
        const { product: productId, design: predesignId, quoteAssetId } = design;

        const params = { productId, predesignId, quoteAssetId };

        const shapeData = {
          shape: shape,
          ...(width && { width: convertToTwips(width) }),
          ...(height && { height: convertToTwips(height) }),
          ...(cornerRadius && {
            cornerRadius: convertToTwips(
              width && height && cornerRadius > Math.min(width, height) / 2 ? Math.min(width, height) / 2 : cornerRadius,
            ),
          }),
        };

        createArtworkProject(params, shapeData)
          .then(projectData => {
            flow.loadProject(projectData);
            resolve(projectData);
          })
          .catch(() => {
            addError('qau-something-went-wrong');
          });
      }),
    [flow, ctx, design, addError, createArtworkProject, convertToTwips],
  );

  const navigateToCustomize = useCallback(async () => {
    uploadArtworkToProject().then(
      ({
         artworkInfo: { artwork_id, name },
         projectData: {
           projectLocation,
           product: { id: productId },
         },
         image: { artwork_status, errorCode, errorMessage },
       }) => {
        if (errorCode) {
          return showMessageError(null, errorCode, errorMessage);
        }
        if (artwork_status && !isValidArtwork({ artwork_status }, flowConfig)) {
          return finishNonReadableArtwork();
        }
        navigateToView(CUSTOMIZE_VIEW, projectLocation, productId, {
          designName: designName || name,
          artworkId: artwork_id,
        });
      },
    );
  }, [designName, uploadArtworkToProject, flowConfig]);

  const proceedToCustomize = useCallback(() => {
    designName === ctx.designName ? navigateToCustomize() : renameArtwork().then(navigateToCustomize);
  }, [artworkInfo, ctx.designName, navigateToCustomize, renameArtwork]);

  const finishNonReadableArtwork = useCallback(() => {
    finishArtwork(flow, context, flowConfig, true).then(artworkData => {
      processFinishedArtwork(artworkData, flow, flowConfig);
    });
  }, [flow, context, flowConfig, finishArtwork, processFinishedArtwork]);

  const showMessageError = (errorLocKey, code, message) => {
    return addError(code ? `qau-error-${code}` : errorLocKey, {
      message: message,
      onClose: resetSelectedFile,
      titleLocKey: 'qau-upload-error-title',
      closeLocKey: 'qau-upload-error-dismiss',
    });
  };

  const artworkCreated = useCallback(
    async artworkData => {
      if (!artworkData) return;

      if (artworkData.artwork_status === 'UPLOAD_ERROR') {
        return addWarning('qau-upload-error', {
          onClose: resetSelectedFile,
        });
      }

      stateArtworkInfo(artworkData);
      setDesignName(artworkData.name);

      if (!artworkProject) {
        try {
          await prepareArtworkProject();
        } catch {
          return;
        }
      }

      if (isValidArtwork(artworkData, flowConfig)) {
        return isSkipNameStep(flowConfig) ? proceedToCustomize() : setShowRenameStep(true);
      }

      const shouldNavigateToApplyCodesView = isApplyCodesViewAvailable(flowConfig);

      if (shouldNavigateToApplyCodesView) {
        const { projectData: { projectLocation, product: { id: productId } } = {} } = flow;
        const { artwork_id } = artworkData;
        return navigateToView(APPLY_CODES_VIEW, projectLocation, productId, {
          designName,
          artworkId: artwork_id,
        });
      }

      const { artwork_status, errors } = artworkData;
      const showUploadWarningStatuses = flowConfig.features.getFeature('showQAUUploadWarning').getAllParamValues();
      const showSpotColorUploadWarning = showUploadWarningStatuses.includes(artwork_status);
      const isUnsupportedNonReadable =
        ['NON_READABLE'].includes(artwork_status) && !flowConfig.features.isFeatureEnabled('supportNonReadableArtwork');

      const trimLineNotSupportedError = parseError(errors, 476);

      if (trimLineNotSupportedError) {
        return navigateToErrorMode(trimLineNotSupportedError.code);
      }

      if (showSpotColorUploadWarning) {
        return showMessageError('qau-error-default');
      }

      if (isUnsupportedNonReadable) {
        const errorCodes = [420, 419, 437, 421, 430, 472];
        !isRollFlow(flowConfig) && errorCodes.push(470);
        const { code, message } = parseError(errors, errorCodes);
        if (code || message) {
          return showMessageError(null, code, message);
        }
      }

      finishNonReadableArtwork();
    },
    [
      ctx,
      flow,
      flowConfig,
      designName,
      addError,
      addWarning,
      artworkProject,
      stateArtworkInfo,
      resetSelectedFile,
      proceedToCustomize,
      prepareArtworkProject,
      finishNonReadableArtwork,
    ],
  );

  const pollArtworkStatus = useCallback(
    assignedArtworkId => {
      const pollArtworkStatus = () =>
        new Promise(resolve => {
          fetchArtworkUploadResult(assignedArtworkId).then(({ data: artworkData } = {}) => {
            artworkData ? artworkCreated(artworkData) : setTimeout(pollArtworkStatus, 1000);
          });
        });

      startLoading();
      pollArtworkStatus();
      setAssignedArtworkId(null);
    },
    [fetchArtworkUploadResult, setAssignedArtworkId, artworkCreated, startLoading],
  );

  const resetArtworkUpload = useCallback(() => {
    flow.editor = null;
    context.project = null;
    resetArtworkInfo();
    resetArtworkImage();
    resetCustomizeData();
  }, [
    flow,
    context,
    resetArtworkInfo,
    resetArtworkImage,
    resetCustomizeData
  ]);

  const loadOrCreateProject = useCallback(() => {
    const { artworkId } = ctx;
    const { storage: storageId, path: relativePath } = customize;

    const projectLocation = storageId && relativePath ? {
      storageId,
      relativePath,
    } : {};

    if (artworkId) {
      startLoading();
      flow.loadProjectByArtworkId(
        artworkId,
        projectLocation,
        ({ data: projectData }) => {
          stateArtworkProject(projectData);
          flow.loadProject(projectData);
          endLoading();
        },
        errors => {
          const error = (errors && errors[0]) || {};
          error.code === 22 ? prepareArtworkProject() : console.error(error);
          endLoading();
        },
        this,
      );
    } else {
      prepareArtworkProject();
    }
  }, [artworkProject, ctx, customize, endLoading, flow, prepareArtworkProject, startLoading, stateArtworkProject]);

  useEffect(() => {
    resetArtworkUpload();
    loadOrCreateProject();
  }, []);

  useEffect(() => {
    const { artworkId: artwork_id, designName } = ctx;
    stateArtworkInfo({ artwork_id, designName });
    setDesignName(designName);
  }, [ctx, stateArtworkInfo]);

  useEffect(() => {
    const { product: productId } = design;
    const { behavior = 'upload' } = ctx;
    const updatedArworkInfo = { ...artworkInfo, ...selectedFileData };

    if (selectedFileData) {
      setFileData(null);
      stateArtworkInfo(updatedArworkInfo);
      !designName && setDesignName(getFileName(updatedArworkInfo.file));
      processArtworkUpload(updatedArworkInfo, productId, behavior);
    }
  }, [design, ctx, processArtworkUpload, artworkInfo, stateArtworkInfo, selectedFileData]);

  useEffect(() => {
    assignedArtworkId && pollArtworkStatus(assignedArtworkId);
  }, [assignedArtworkId, pollArtworkStatus]);

  useEffect(() => {
    renameDesign(designName);
  }, [designName, renameDesign]);

  useEffect(() => {
    setFooterConfig({
      visible: showRenameStep,
      onBack: resetSelectedFile,
      onNext: proceedToCustomize,
      backHidden: false,
      nextHidden: false,
      nextDisabled: !designName?.trim(),
      nextLocKey: 'qau-next-btn',
    });
  }, [designName, setFooterConfig, showRenameStep, proceedToCustomize, resetSelectedFile]);

  const onFileSelected = file => {
    isFileFormatInvalid(file, flowConfig)
      ? isStickerFlow(flowConfig) || isSheetFlow(flowConfig)
        ? showMessageError('qau-error-default')
        : addWarning('qau-wrong-image-type-warning', {
            onClose: resetSelectedFile,
            closeOnOutsideClick: true,
          })
      : setFileData({
          file: file,
          designName: getFileName(file),
          artwork_file_type: getFileType(file),
        });
  };

  const navigateToErrorMode = errorCode => {
    const { projectData: { projectLocation, product: { id: productId } } = {} } = flow;

    navigateToView(ERROR_MODE_VIEW, projectLocation, productId, { errorCode });
  };

  return (
    <div id="qau-upload-view" className={classnames({ upload: !showRenameStep })}>
      <div className="upload-title">
        <LocalizedText id="qau-title-upload-art" />
      </div>
      {showRenameStep ? (
        <RenameDesign designName={designName} setDesignName={setDesignName} />
      ) : (
        <UploadArtwork handleFileSelect={onFileSelected} resetSelectedFile={resetSelectedFile} isMobileView={isMobileMode(ctx)} />
      )}
    </div>
  );
};

const mapStateToProps = ({ matrix, artwork, upload }) => ({
  assignedArtworkId: upload.assignedArtworkId,
  artworkProject: artwork.project,
  artworkInfo: artwork.info,
  matrix,
});

const mapDispatchToProps = {
  processFinishedArtwork,
  processArtworkUpload,
  createArtworkProject,
  stateArtworkProject,
  setAssignedArtworkId,
  fetchArtworkUploadResult,
  uploadArtworkToProject,
  resetArtworkImage,
  resetArtworkInfo,
  resetCustomizeData,
  stateArtworkInfo,
  setFooterConfig,
  renameArtwork,
  finishArtwork,
  startLoading,
  renameDesign,
  endLoading,
  addWarning,
  addError,
};

UploadView.propTypes = {
  matrix: PropTypes.object,
  artworkInfo: PropTypes.object,
  artworkProject: PropTypes.object,
  flow: PropTypes.object.isRequired,
  assignedArtworkId: PropTypes.string,
  addError: PropTypes.func.isRequired,
  context: PropTypes.object.isRequired,
  addWarning: PropTypes.func.isRequired,
  endLoading: PropTypes.func.isRequired,
  startLoading: PropTypes.func.isRequired,
  flowConfig: PropTypes.object.isRequired,
  renameDesign: PropTypes.func.isRequired,
  renameArtwork: PropTypes.func.isRequired,
  finishArtwork: PropTypes.func.isRequired,
  stateArtworkInfo: PropTypes.func.isRequired,
  setFooterConfig: PropTypes.func.isRequired,
  resetArtworkInfo: PropTypes.func.isRequired,
  resetArtworkImage: PropTypes.func.isRequired,
  uploadArtworkToProject: PropTypes.func.isRequired,
  stateArtworkProject: PropTypes.func.isRequired,
  createArtworkProject: PropTypes.func.isRequired,
  fetchArtworkUploadResult: PropTypes.func.isRequired,
  setAssignedArtworkId: PropTypes.func.isRequired,
  processArtworkUpload: PropTypes.func.isRequired,
  processFinishedArtwork: PropTypes.func.isRequired,
};

export default inject('flowConfig', 'flow', 'context')(connect(mapStateToProps, mapDispatchToProps)(UploadView));
